import { Dropdown, Form, theme } from "antd";
import type { MenuProps } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  type Icon,
  LinkIcon,
  EnvelopeSimpleIcon,
  CalendarBlankIcon,
  PhoneIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { IconButton, IconChip, Input, Popconfirm } from "@libertify/ui";
import type { ExperienceLinkType } from "../types";
import { NEW_LINK_TYPE } from "../defaults";
import { hasLinkContent } from "./headerLinksMapping";
import {
  LinkRow,
  BareField,
  RowSeparator,
  TypeTrigger,
} from "./ExperienceLinkItem.styles";

const KEY = "createExperience.generalSettings.links";

/** Row icon per CTA type. */
const LINK_TYPE_ICON: Record<ExperienceLinkType, Icon> = {
  link: LinkIcon,
  email: EnvelopeSimpleIcon,
  calendar: CalendarBlankIcon,
  phone: PhoneIcon,
};

/** Order the CTA types appear in the type-picker dropdown. */
const LINK_TYPES: ExperienceLinkType[] = ["link", "email", "calendar", "phone"];

/** i18n key for the value input's placeholder per CTA type. */
const LINK_VALUE_PLACEHOLDER_KEY: Record<ExperienceLinkType, string> = {
  link: `${KEY}.valuePlaceholder.link`,
  email: `${KEY}.valuePlaceholder.email`,
  calendar: `${KEY}.valuePlaceholder.calendar`,
  phone: `${KEY}.valuePlaceholder.phone`,
};

/**
 * The leading icon-chip, upgraded to a dropdown that picks the link's CTA type.
 * Acts as a Form.Item control — `value` is the current type and `onChange`
 * commits the pick, so the change flows through the form's `onValuesChange` into
 * the draft (a plain `form.setFieldValue` would not). The open menu highlights
 * the current type.
 */
const LinkTypePicker = ({
  value,
  onChange,
}: {
  value?: ExperienceLinkType;
  onChange?: (value: ExperienceLinkType) => void;
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const current = value ?? NEW_LINK_TYPE;
  const CurrentIcon = LINK_TYPE_ICON[current];

  const items: MenuProps["items"] = LINK_TYPES.map((type) => {
    const ItemIcon = LINK_TYPE_ICON[type];
    return { key: type, icon: <ItemIcon size={16} />, label: t(`${KEY}.type.${type}`) };
  });

  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items,
        selectable: true,
        selectedKeys: [current],
        onClick: ({ key }) => onChange?.(key as ExperienceLinkType),
      }}
    >
      <TypeTrigger type="button" aria-label={t(`${KEY}.type.select`)}>
        <IconChip
          size="s"
          color={token.colorTextSecondary}
          icon={<CurrentIcon />}
        />
      </TypeTrigger>
    </Dropdown>
  );
};

interface ExperienceLinkItemProps {
  /** Index of this link within the `links` Form.List. */
  name: number;
  onRemove: () => void;
  /** Guard removal behind a Popconfirm — on for the edit card, where a
   *  removed link is gone for real (patched right away), off (default) for
   *  the create card, where nothing is persisted until the draft submits. */
  confirmRemove?: boolean;
  /** Ids of links actually saved on the backend — see `HeaderLinksSection`. */
  savedLinkIds?: Set<string>;
  /** Fired when the label or value input blurs. No-op if omitted. */
  onFieldBlur?: () => void;
}

/**
 * One experience-link (header CTA) row: a type picker, an editable display
 * name, the target value and a remove button — no on/off state, the backend
 * `CallToAction` has no such field, so a link is either present or removed.
 * The value input's placeholder follows the row's `type`, read from the
 * surrounding Form.
 */
export const ExperienceLinkItem = ({
  name,
  onRemove,
  confirmRemove,
  savedLinkIds,
  onFieldBlur,
}: ExperienceLinkItemProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const id: string | undefined = Form.useWatch(["links", name, "id"]);
  const type: ExperienceLinkType =
    Form.useWatch(["links", name, "type"]) ?? NEW_LINK_TYPE;
  const label: string = Form.useWatch(["links", name, "label"]) ?? "";
  const value: string = Form.useWatch(["links", name, "value"]) ?? "";
  // A row that's still missing a label or value can't be saved anyway (see
  // `hasLinkContent`) — nothing real to lose. But a row whose fields were
  // just cleared without saving is still `isSavedLink` — still needs a confirm.
  const isSavedLink = Boolean(id && savedLinkIds?.has(id));
  const needsConfirm =
    confirmRemove && (hasLinkContent({ type, label, value }) || isSavedLink);

  const removeButton = (
    <IconButton
      className="link-remove"
      variant="danger"
      size="s"
      aria-label={t(`${KEY}.remove`)}
      icon={<TrashIcon weight="bold" />}
      onClick={needsConfirm ? undefined : onRemove}
    />
  );

  return (
    <LinkRow $token={token}>
      {/* Registers the path so `Form.useWatch(["links", name, "id"])` above
          actually reflects it — an unregistered path always watches as
          undefined. */}
      <Form.Item name={[name, "id"]} noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name={[name, "type"]} noStyle>
        <LinkTypePicker />
      </Form.Item>
      <BareField>
        <Form.Item name={[name, "label"]} noStyle>
          <Input
            variant="borderless"
            placeholder={t(`${KEY}.namePlaceholder`)}
            onBlur={onFieldBlur}
          />
        </Form.Item>
      </BareField>
      <RowSeparator $token={token} />
      <BareField>
        <Form.Item name={[name, "value"]} noStyle>
          <Input
            variant="borderless"
            placeholder={t(LINK_VALUE_PLACEHOLDER_KEY[type])}
            onBlur={onFieldBlur}
          />
        </Form.Item>
      </BareField>
      {needsConfirm ? (
        <Popconfirm
          title={t(`${KEY}.removeConfirm`)}
          onConfirm={onRemove}
          confirmText={t("buttons.delete")}
          cancelText={t("buttons.cancel")}
          danger
        >
          {removeButton}
        </Popconfirm>
      ) : (
        removeButton
      )}
    </LinkRow>
  );
};

export default ExperienceLinkItem;
